import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Portal,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import axios from '../../../axiosConfig';
import { useUser } from '../../../contexts/UserContext';
import { getUserRoles } from '../../../utils/authRoles';
import OrganizationDetailHeader from '../../organization/OrganizationDetailHeader';
import ConsultantForm from './ConsultantForm';
import { assignAdminConsultantOwner, getAdminConsultant, swapAdminConsultantOwner } from './adminConsultantsApi';

const USER_TYPES = {
  owner: 'owner',
  advisor: 'advisor',
};

const MAX_VISIBLE_CONTACT_LENGTH = 50;

const normalizeApiErrors = (error) => {
  const rawErrors = error?.response?.data?.errors;
  if (!rawErrors) return { base: 'Could not save. Please try again.' };

  if (Array.isArray(rawErrors)) {
    return { base: rawErrors.join(', ') };
  }

  if (typeof rawErrors === 'object') {
    return Object.entries(rawErrors).reduce((acc, [key, value]) => {
      acc[key] = Array.isArray(value) ? value.join(', ') : String(value);
      return acc;
    }, {});
  }

  return { base: 'Could not save. Please try again.' };
};

const parseAdvisorsResponse = (data) => {
  if (Array.isArray(data)) return data;
  return data?.advisors || [];
};

const normalizeEmail = (value) => (value || '').trim().toLowerCase();

const getOwnerLinkedUserId = (ownerRecord) => {
  const rawUserId = ownerRecord?.user_id ?? ownerRecord?.id ?? null;
  return rawUserId ? Number(rawUserId) : null;
};

const getAdvisorLinkedUserId = (advisorRecord) => {
  const rawUserId = advisorRecord?.user_id ?? null;
  return rawUserId ? Number(rawUserId) : null;
};

const isAdvisorMatchingOwner = (advisor, owner) => {
  if (!advisor || !owner) return false;
  const ownerUserId = getOwnerLinkedUserId(owner);
  const advisorUserId = getAdvisorLinkedUserId(advisor);
  if (ownerUserId && advisorUserId && advisorUserId === ownerUserId) return true;
  const advisorEmail = normalizeEmail(advisor.email);
  const ownerEmail = normalizeEmail(owner.email);
  return Boolean(advisorEmail && ownerEmail && advisorEmail === ownerEmail);
};

const truncateValue = (value, maxLength = MAX_VISIBLE_CONTACT_LENGTH) => {
  if (!value) return '—';
  return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
};

const getUserEditPath = (userId) => (userId ? `/app/users/edit/${userId}` : null);

const LabeledValue = ({ label, value, title, isLink = false }) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" letterSpacing="wider" color="gray.500" mb={1}>
      {label}
    </Text>
    {isLink && value && value !== '—' ? (
      <Text
        as="a"
        href={value}
        target="_blank"
        rel="noreferrer"
        color="blue.600"
        fontWeight="medium"
        title={title || value}
        wordBreak="break-word"
      >
        {value}
      </Text>
    ) : (
      <Text color={value === '—' ? 'gray.500' : 'gray.800'} title={title}>
        {value}
      </Text>
    )}
  </Box>
);

const buildUserFormValues = (user) => {
  if (!user) {
    return {
      type: USER_TYPES.advisor,
      name: '',
      first_name: '',
      last_name: '',
      email: '',
      slug: '',
      booking_url: '',
      active: true,
    };
  }

  return {
    type: user.type,
    name: user.name || '',
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email: user.email || '',
    slug: user.slug || '',
    booking_url: user.booking_url || '',
    active: user.active ?? true,
  };
};

const ConsultantUserModal = ({
  isOpen,
  onClose,
  user,
  onSubmit,
  isSubmitting,
  errors,
}) => {
  const [formValues, setFormValues] = useState(buildUserFormValues(user));
  const [localErrors, setLocalErrors] = useState({});

  useEffect(() => {
    setFormValues(buildUserFormValues(user));
  }, [user, isOpen]);

  useEffect(() => {
    setLocalErrors(errors || {});
  }, [errors]);

  const isEditing = Boolean(user);
  const isOwnerRow = user?.type === USER_TYPES.owner;
  const canDemoteOwner = !isOwnerRow;
  const selectedType = formValues.type;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'type' && value === USER_TYPES.owner
        ? { name: prev.name || [prev.first_name, prev.last_name].filter(Boolean).join(' ').trim() }
        : {}),
    }));
    if (localErrors[name]) {
      setLocalErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleActiveChange = (event) => {
    setFormValues((prev) => ({ ...prev, active: event.target.checked }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formValues.email.trim()) nextErrors.email = 'Email is required';

    if (selectedType === USER_TYPES.owner) {
      if (!formValues.first_name.trim() && !formValues.last_name.trim() && !isEditing) {
        nextErrors.first_name = 'First or last name is recommended for owner records';
      }
    } else if (!formValues.name.trim()) {
      nextErrors.name = 'Name is required';
    }

    if (isOwnerRow && selectedType === USER_TYPES.advisor) {
      nextErrors.type = 'Direct owner demotion is not supported from Face with current APIs.';
    }

    return nextErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setLocalErrors(validationErrors);
      return;
    }

    onSubmit({
      ...formValues,
      name: formValues.name.trim(),
      first_name: formValues.first_name.trim(),
      last_name: formValues.last_name.trim(),
      email: formValues.email.trim(),
      slug: formValues.slug.trim(),
      booking_url: formValues.booking_url.trim(),
      active: Boolean(formValues.active),
    });
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <Box
        position="fixed"
        inset={0}
        bg="blackAlpha.600"
        zIndex={1400}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <Box
          role="dialog"
          aria-modal="true"
          bg="white"
          borderRadius="xl"
          shadow="xl"
          w="100%"
          maxW="2xl"
          maxH="calc(100vh - 2rem)"
          overflowY="auto"
          p={6}
        >
          <Flex align="center" justify="space-between" mb={6} gap={4}>
            <Heading size="md">{isEditing ? 'Edit User' : 'Add User'}</Heading>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </Flex>

          <VStack spacing={4} align="stretch">
            <FormControl isInvalid={Boolean(localErrors.type)}>
              <FormLabel>Type</FormLabel>
              <Select
                name="type"
                value={selectedType}
                onChange={handleChange}
              >
                <option value={USER_TYPES.owner}>Owner/Admin</option>
                <option value={USER_TYPES.advisor} disabled={!canDemoteOwner}>
                  Advisor
                </option>
              </Select>
              {!canDemoteOwner ? (
                <FormHelperText>Owner/Admin cannot be directly demoted to Advisor with the current Face API flow.</FormHelperText>
              ) : null}
              <FormErrorMessage>{localErrors.type}</FormErrorMessage>
            </FormControl>

            {selectedType === USER_TYPES.owner ? (
              <>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isInvalid={Boolean(localErrors.first_name)}>
                    <FormLabel>First Name</FormLabel>
                    <Input
                      name="first_name"
                      placeholder="First name"
                      value={formValues.first_name}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>{localErrors.first_name}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={Boolean(localErrors.last_name)}>
                    <FormLabel>Last Name</FormLabel>
                    <Input
                      name="last_name"
                      placeholder="Last name"
                      value={formValues.last_name}
                      onChange={handleChange}
                    />
                    <FormErrorMessage>{localErrors.last_name}</FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                <FormControl isRequired isInvalid={Boolean(localErrors.email)}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    placeholder="owner@example.com"
                    value={formValues.email}
                    onChange={handleChange}
                  />
                  <FormHelperText>Owner/Admin records currently support email and name fields in Face.</FormHelperText>
                  <FormErrorMessage>{localErrors.email}</FormErrorMessage>
                </FormControl>
              </>
            ) : (
              <>
                <FormControl isRequired isInvalid={Boolean(localErrors.name)}>
                  <FormLabel>Name</FormLabel>
                  <Input
                    name="name"
                    placeholder="User name"
                    value={formValues.name}
                    onChange={handleChange}
                  />
                  <FormErrorMessage>{localErrors.name}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(localErrors.email)}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    placeholder="advisor@example.com"
                    value={formValues.email}
                    onChange={handleChange}
                  />
                  <FormErrorMessage>{localErrors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(localErrors.slug)}>
                  <FormLabel>Slug</FormLabel>
                  <Input
                    name="slug"
                    placeholder="advisor-slug"
                    value={formValues.slug}
                    onChange={handleChange}
                  />
                  <FormErrorMessage>{localErrors.slug}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(localErrors.booking_url)}>
                  <FormLabel>Booking URL</FormLabel>
                  <Input
                    name="booking_url"
                    placeholder="https://..."
                    value={formValues.booking_url}
                    onChange={handleChange}
                  />
                  <FormErrorMessage>{localErrors.booking_url}</FormErrorMessage>
                </FormControl>

                <FormControl display="flex" alignItems="center" isInvalid={Boolean(localErrors.active)}>
                  <FormLabel htmlFor="consultant-user-active" mb="0">
                    Active
                  </FormLabel>
                  <Switch
                    id="consultant-user-active"
                    isChecked={formValues.active}
                    onChange={handleActiveChange}
                    colorScheme="green"
                  />
                  <FormErrorMessage>{localErrors.active}</FormErrorMessage>
                </FormControl>
              </>
            )}

            {localErrors.base ? (
              <Text color="red.500" fontSize="sm">
                {localErrors.base}
              </Text>
            ) : null}

            <Flex justify="flex-end" gap={3}>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting}>
                {isEditing ? 'Save User' : 'Add User'}
              </Button>
            </Flex>
          </VStack>
        </Box>
      </Box>
    </Portal>
  );
};

const ConsultantEditPage = () => {
  const { id } = useParams();
  const toast = useToast();
  const { user, isLoading: isUserLoading } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingConsultant, setIsSubmittingConsultant] = useState(false);
  const [consultantErrors, setConsultantErrors] = useState({});
  const [consultant, setConsultant] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [isLoadingAdvisors, setIsLoadingAdvisors] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [userErrors, setUserErrors] = useState({});
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [deleteAdvisorId, setDeleteAdvisorId] = useState(null);
  const [isDeletingAdvisor, setIsDeletingAdvisor] = useState(false);
  const cancelDeleteAdvisorRef = useRef(null);
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure();
  const {
    isOpen: isConsultantModalOpen,
    onOpen: onConsultantModalOpen,
    onClose: onConsultantModalClose,
  } = useDisclosure();
  const {
    isOpen: isUserModalOpen,
    onOpen: onUserModalOpen,
    onClose: onUserModalClose,
  } = useDisclosure();
  const isHkeAdmin = useMemo(() => getUserRoles(user).includes('hke_admin'), [user]);

  const loadConsultant = useCallback(async () => {
    setIsLoading(true);
    try {
      const consultantRecord = await getAdminConsultant(id);
      setConsultant(consultantRecord);
    } catch (error) {
      console.error('Error loading consultant:', error);
      toast({
        title: 'Could not load consultant',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, toast]);

  const loadAdvisors = useCallback(async () => {
    setIsLoadingAdvisors(true);
    try {
      const response = await axios.get(`/consultants/${id}/advisors`);
      setAdvisors(parseAdvisorsResponse(response?.data));
    } catch (error) {
      console.error('Error loading advisors:', error);
      setAdvisors([]);
      toast({
        title: 'Could not load advisors',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoadingAdvisors(false);
    }
  }, [id, toast]);

  useEffect(() => {
    if (isUserLoading || !isHkeAdmin) return;
    loadConsultant();
    loadAdvisors();
  }, [isHkeAdmin, isUserLoading, loadConsultant, loadAdvisors]);

  const handleSaveConsultant = async (payload) => {
    setIsSubmittingConsultant(true);
    setConsultantErrors({});
    const consultantPayload = {
      name: payload?.name?.trim() || '',
      email: payload?.email?.trim() || '',
      slug: payload?.slug?.trim() || '',
      booking_url: payload?.booking_url?.trim() || '',
      active: Boolean(payload?.active),
    };

    try {
      await axios.patch(`/consultants/${id}`, { consultant: consultantPayload });
      await loadConsultant();
      onConsultantModalClose();
      toast({
        title: 'Consultant updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating consultant:', error);
      setConsultantErrors(normalizeApiErrors(error));
      toast({
        title: 'Could not update consultant',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingConsultant(false);
    }
  };

  const owner = consultant?.owner_user;
  const ownerFullName = useMemo(() => {
    if (!owner) return null;
    return [owner.first_name, owner.last_name].filter(Boolean).join(' ').trim();
  }, [owner]);

  const ownerDisplayName = ownerFullName || owner?.email || null;
  const ownerRow = useMemo(() => {
    if (!owner) return null;
    const linkedUserId = getOwnerLinkedUserId(owner);

    return {
      rowKey: `owner-${linkedUserId || owner.email}`,
      type: USER_TYPES.owner,
      typeLabel: 'Owner/Admin',
      id: owner.id || null,
      name: ownerDisplayName,
      first_name: owner.first_name || '',
      last_name: owner.last_name || '',
      email: owner.email || '',
      slug: '',
      booking_url: owner.booking_url || '',
      active: true,
      user_id: linkedUserId,
    };
  }, [owner, ownerDisplayName]);

  const advisorRows = useMemo(
    () => advisors
      .filter((advisor) => !isAdvisorMatchingOwner(advisor, owner))
      .map((advisor) => ({
        rowKey: `advisor-${advisor.id}`,
        type: USER_TYPES.advisor,
        typeLabel: 'Advisor',
        id: advisor.id,
        name: advisor.name || advisor.email || '—',
        first_name: '',
        last_name: '',
        email: advisor.email || '',
        slug: advisor.slug || '',
        booking_url: advisor.booking_url || '',
        active: advisor.active ?? true,
        user_id: getAdvisorLinkedUserId(advisor),
      })),
    [advisors, owner],
  );

  const users = useMemo(
    () => (ownerRow ? [ownerRow, ...advisorRows] : advisorRows),
    [ownerRow, advisorRows],
  );

  const openAddUser = () => {
    setEditingUser(null);
    setUserErrors({});
    onUserModalOpen();
  };

  const openEditUser = (userRecord) => {
    setEditingUser(userRecord);
    setUserErrors({});
    onUserModalOpen();
  };

  const assignOwnerWithFallback = useCallback(async ({ email, first_name, last_name }) => {
    const payload = { email };
    if (first_name) payload.first_name = first_name;
    if (last_name) payload.last_name = last_name;

    try {
      return await assignAdminConsultantOwner(id, payload);
    } catch (error) {
      const errors = error?.response?.data?.errors;
      const errorsAsText = Array.isArray(errors)
        ? errors.join(' ').toLowerCase()
        : typeof errors === 'object'
          ? Object.values(errors).flat().join(' ').toLowerCase()
          : '';
      const namesUnsupported = errorsAsText.includes('first_name')
        || errorsAsText.includes('last_name')
        || errorsAsText.includes('unknown attribute')
        || errorsAsText.includes('unpermitted');

      if (!namesUnsupported || (!payload.first_name && !payload.last_name)) {
        throw error;
      }

      return assignAdminConsultantOwner(id, { email });
    }
  }, [id]);

  const handleSaveUser = async (payload) => {
    setIsSavingUser(true);
    setUserErrors({});

    try {
      if (payload.type === USER_TYPES.owner) {
        if (editingUser?.type === USER_TYPES.advisor) {
          const advisorPayload = {
            name: payload.name || editingUser.name || '',
            email: payload.email,
            slug: payload.slug || '',
            booking_url: payload.booking_url || '',
            active: Boolean(payload.active),
          };
          await axios.patch(`/consultants/${id}/advisors/${editingUser.id}`, { advisor: advisorPayload });
          await swapAdminConsultantOwner(id, { advisor_id: editingUser.id });
        } else {
          await assignOwnerWithFallback({
            email: payload.email,
            first_name: payload.first_name,
            last_name: payload.last_name,
          });
        }
      } else {
        const advisorPayload = {
          name: payload.name,
          email: payload.email,
          slug: payload.slug,
          booking_url: payload.booking_url,
          active: Boolean(payload.active),
        };

        if (editingUser?.type === USER_TYPES.advisor && editingUser.id) {
          await axios.patch(`/consultants/${id}/advisors/${editingUser.id}`, { advisor: advisorPayload });
        } else {
          await axios.post(`/consultants/${id}/advisors`, { advisor: advisorPayload });
        }
      }

      await Promise.all([loadConsultant(), loadAdvisors()]);
      onUserModalClose();
      toast({
        title: editingUser ? 'User updated' : 'User added',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving consultant user:', error);
      setUserErrors(normalizeApiErrors(error));
      toast({
        title: 'Could not save user',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSavingUser(false);
    }
  };

  const openDeleteAdvisor = (advisorId) => {
    setDeleteAdvisorId(advisorId);
    onDeleteDialogOpen();
  };

  const handleDeleteAdvisor = async () => {
    if (!deleteAdvisorId) return;
    setIsDeletingAdvisor(true);
    try {
      await axios.delete(`/consultants/${id}/advisors/${deleteAdvisorId}`);
      await loadAdvisors();
      setDeleteAdvisorId(null);
      onDeleteDialogClose();
      toast({
        title: 'User removed',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting advisor:', error);
      toast({
        title: 'Could not remove user',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsDeletingAdvisor(false);
    }
  };

  const activeBadge = useMemo(
    () => (active) => <Badge colorScheme={active ? 'green' : 'gray'}>{active ? 'Active' : 'Inactive'}</Badge>,
    [],
  );

  if (isUserLoading) {
    return (
      <Box p={6}>
        <Spinner />
      </Box>
    );
  }

  if (!isHkeAdmin) {
    return <Navigate to="/app/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <Box p={6}>
        <Spinner />
      </Box>
    );
  }

  if (!consultant) {
    return (
      <Box p={6}>
        <Heading size="md">Consultant not found</Heading>
      </Box>
    );
  }

  return (
    <Box p={6} maxW="6xl" mx="auto">
      <OrganizationDetailHeader
        title={consultant.name || 'Consultant'}
        subtitle="Consultant details"
        actions={[
          { label: 'Edit Consultant', onClick: onConsultantModalOpen, colorScheme: 'blue' },
          { label: 'Add User', onClick: openAddUser, colorScheme: 'green' },
        ]}
      />

      <Stack spacing={6}>
        <Card shadow="sm" borderRadius="xl">
          <CardHeader pb={0}>
            <Flex align="center" justify="space-between" gap={4} wrap="wrap">
              <Box>
                <Heading size="md">General Information</Heading>
                <Text color="gray.500" mt={1}>
                  Core consultant details and public-facing contact information.
                </Text>
              </Box>
              {activeBadge(consultant.active)}
            </Flex>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <LabeledValue label="Name" value={consultant.name || '—'} />
              <LabeledValue label="Slug" value={consultant.slug || '—'} />
              <LabeledValue label="Email" value={consultant.email || '—'} title={consultant.email || undefined} />
              <LabeledValue label="Contact" value={consultant.contact || consultant.phone || '—'} />
              <LabeledValue
                label="Booking URL"
                value={consultant.booking_url || '—'}
                title={consultant.booking_url || undefined}
                isLink={Boolean(consultant.booking_url)}
              />
              <LabeledValue label="Consultant ID" value={String(consultant.id ?? '—')} />
            </SimpleGrid>
          </CardBody>
        </Card>

        <Card shadow="sm" borderRadius="xl">
          <CardHeader pb={0}>
            <Flex align="center" justify="space-between" gap={4} wrap="wrap">
              <Box>
                <Heading size="md">Users</Heading>
                <Text color="gray.500" mt={1}>
                  All consultant-linked users, including Owner/Admin and Advisor records.
                </Text>
              </Box>
              <Button colorScheme="green" variant="outline" leftIcon={<FaPlus />} onClick={openAddUser}>
                Add User
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoadingAdvisors ? (
              <Box textAlign="center" py={8}>
                <Spinner />
              </Box>
            ) : users.length === 0 ? (
              <Box borderWidth="1px" borderStyle="dashed" borderColor="gray.200" borderRadius="lg" p={6}>
                <Text fontWeight="semibold" mb={1}>
                  No users linked yet.
                </Text>
                <Text color="gray.600" mb={4}>
                  Add an Owner/Admin or Advisor to manage this consultant.
                </Text>
                <Button colorScheme="green" variant="outline" leftIcon={<FaPlus />} onClick={openAddUser}>
                  Add User
                </Button>
              </Box>
            ) : (
              <>
                <Box display={{ base: 'none', md: 'block' }}>
                  <Box
                    w="100%"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    bg="white"
                    overflow="hidden"
                  >
                    <TableContainer>
                      <Table
                        variant="unstyled"
                        sx={{
                          'th, td': { borderBottom: '1px solid', borderColor: 'gray.100' },
                          'tbody tr:hover': { background: 'gray.50' },
                        }}
                      >
                        <Thead>
                          <Tr>
                            <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Name</Th>
                            <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Email</Th>
                            <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Type</Th>
                            <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Booking URL</Th>
                            <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {users.map((userRow) => (
                            <Tr key={userRow.rowKey}>
                              <Td py={3} fontWeight="semibold" maxW="240px">
                                {getUserEditPath(userRow.user_id) ? (
                                  <Link
                                    as={RouterLink}
                                    to={getUserEditPath(userRow.user_id)}
                                    color="blue.600"
                                    title={userRow.name || undefined}
                                    whiteSpace="nowrap"
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    display="block"
                                  >
                                    {userRow.name || '—'}
                                  </Link>
                                ) : (
                                  <Text title={userRow.name || undefined} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                                    {userRow.name || '—'}
                                  </Text>
                                )}
                              </Td>
                              <Td py={3} maxW="260px">
                                <Text title={userRow.email || undefined} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                                  {truncateValue(userRow.email)}
                                </Text>
                              </Td>
                              <Td py={3}>
                                <Badge colorScheme={userRow.type === USER_TYPES.owner ? 'blue' : 'purple'}>
                                  {userRow.typeLabel}
                                </Badge>
                              </Td>
                              <Td py={3} maxW="240px">
                                {userRow.booking_url ? (
                                  <Text
                                    as="a"
                                    href={userRow.booking_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    color="blue.600"
                                    title={userRow.booking_url}
                                    whiteSpace="nowrap"
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    display="block"
                                  >
                                    {truncateValue(userRow.booking_url)}
                                  </Text>
                                ) : (
                                  <Text color="gray.500">—</Text>
                                )}
                              </Td>
                              <Td py={3}>
                                <HStack spacing={2}>
                                  <Tooltip label="Edit" hasArrow>
                                    <IconButton
                                      icon={<FaPencilAlt />}
                                      aria-label={`Edit ${userRow.name || userRow.email}`}
                                      variant="outline"
                                      size="sm"
                                      onClick={() => openEditUser(userRow)}
                                    />
                                  </Tooltip>
                                  {userRow.type !== USER_TYPES.owner && (
                                    <Tooltip label="Remove" hasArrow>
                                      <IconButton
                                        icon={<FaTrash />}
                                        aria-label={`Remove ${userRow.name || userRow.email}`}
                                        variant="outline"
                                        size="sm"
                                        colorScheme="red"
                                        onClick={() => openDeleteAdvisor(userRow.id)}
                                      />
                                    </Tooltip>
                                  )}
                                </HStack>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>

                <Stack spacing={4} display={{ base: 'flex', md: 'none' }}>
                  {users.map((userRow) => (
                    <Box key={userRow.rowKey} borderWidth="1px" borderRadius="lg" p={4}>
                      <VStack align="start" spacing={2}>
                        <Flex justify="space-between" align="start" w="100%" gap={3}>
                          {getUserEditPath(userRow.user_id) ? (
                            <Link
                              as={RouterLink}
                              to={getUserEditPath(userRow.user_id)}
                              fontWeight="semibold"
                              color="blue.600"
                            >
                              {userRow.name || '—'}
                            </Link>
                          ) : (
                            <Text fontWeight="semibold">{userRow.name || '—'}</Text>
                          )}
                          <Badge colorScheme={userRow.type === USER_TYPES.owner ? 'blue' : 'purple'}>
                            {userRow.typeLabel}
                          </Badge>
                        </Flex>
                        <Text fontSize="sm" title={userRow.email || undefined}>
                          Email: {truncateValue(userRow.email)}
                        </Text>
                        <Text fontSize="sm" title={userRow.booking_url || undefined}>
                          Booking URL: {truncateValue(userRow.booking_url)}
                        </Text>
                        <HStack pt={2}>
                          <Button size="sm" variant="outline" onClick={() => openEditUser(userRow)}>
                            Edit
                          </Button>
                          {userRow.type !== USER_TYPES.owner && (
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="red"
                              onClick={() => openDeleteAdvisor(userRow.id)}
                            >
                              Remove
                            </Button>
                          )}
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </CardBody>
        </Card>
      </Stack>

      {isConsultantModalOpen ? (
        <Portal>
          <Box
            position="fixed"
            inset={0}
            bg="blackAlpha.600"
            zIndex={1400}
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={4}
          >
            <Box
              role="dialog"
              aria-modal="true"
              bg="white"
              borderRadius="xl"
              shadow="xl"
              w="100%"
              maxW="3xl"
              maxH="calc(100vh - 2rem)"
              overflowY="auto"
            >
              <Flex align="center" justify="space-between" px={6} py={4} borderBottomWidth="1px">
                <Heading size="md">Edit Consultant</Heading>
                <Button variant="ghost" onClick={onConsultantModalClose}>
                  Close
                </Button>
              </Flex>
              <Box p={6}>
                <ConsultantForm
                  title="Edit Consultant"
                  initialData={consultant}
                  onSubmit={handleSaveConsultant}
                  onCancel={onConsultantModalClose}
                  isSubmitting={isSubmittingConsultant}
                  submitLabel="Save"
                  apiErrors={consultantErrors}
                  showCard={false}
                  hideCancel
                />
              </Box>
            </Box>
          </Box>
        </Portal>
      ) : null}

      <ConsultantUserModal
        isOpen={isUserModalOpen}
        onClose={onUserModalClose}
        user={editingUser}
        onSubmit={handleSaveUser}
        isSubmitting={isSavingUser}
        errors={userErrors}
      />

      <Modal isOpen={isDeleteDialogOpen} onClose={onDeleteDialogClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Remove user</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>This will remove the advisor from this consultant.</Text>
          </ModalBody>
          <Flex justify="flex-end" gap={3} px={6} pb={6}>
            <Button ref={cancelDeleteAdvisorRef} variant="outline" onClick={onDeleteDialogClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDeleteAdvisor} isLoading={isDeletingAdvisor}>
              Remove
            </Button>
          </Flex>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ConsultantEditPage;
